import { RequestService } from '../request-service';
import type RpcModule from '@wix/ambassador-connectors-v1-connector/rpc';
import type HttpModule from '@wix/ambassador-connectors-v1-connector/http';
import type { ConnectPayload } from './types';

type RpcModuleType = typeof RpcModule;
type HttpModuleType = typeof HttpModule;

const ACTIVE_CREDENTIAL_STATE = 'ACTIVE';
const PAGE_LIMIT = 100;

const connectedKey = (appId: string, compId: string): string =>
  `${appId}:${compId}`;

interface QueriedConnector {
  id?: string;
  appId?: string;
  componentId?: string;
  credentialState?: string;
}

interface QueryConnectorsResponse {
  connectors?: QueriedConnector[];
  pagingMetadata?: { hasNext?: boolean; cursors?: { next?: string } };
}

export class ConnectorConnectionsService extends RequestService<
  RpcModuleType,
  HttpModuleType
> {
  protected async rpcModule(): Promise<RpcModuleType> {
    return import('@wix/ambassador-connectors-v1-connector/rpc');
  }

  protected async httpModule(): Promise<HttpModuleType> {
    return import('@wix/ambassador-connectors-v1-connector/http');
  }

  public async getConnectedKeysByComponentIds(
    componentIds: string[],
  ): Promise<Set<string>> {
    const keys = new Set<string>();
    if (componentIds.length === 0) {
      return keys;
    }

    const response = await this.queryConnectors({
      filter: {
        componentId: { $in: componentIds },
        credentialState: ACTIVE_CREDENTIAL_STATE,
      },
      limit: componentIds.length,
    });

    for (const connector of response.connectors ?? []) {
      if (connector.appId && connector.componentId) {
        keys.add(connectedKey(connector.appId, connector.componentId));
      }
    }
    return keys;
  }

  public async queryConnections({
    limit,
    cursor,
  }: {
    limit?: number;
    cursor?: string;
  }): Promise<{
    connectors: { appId: string; compId: string; id: string }[];
    hasNext: boolean;
    nextCursor?: string;
  }> {
    const response = await this.queryConnectors({
      filter: { credentialState: ACTIVE_CREDENTIAL_STATE },
      cursor,
      limit,
    });
    const connectors = (response.connectors ?? [])
      .filter((c) => c.appId && c.componentId && c.id)
      .map((c) => ({
        appId: c.appId as string,
        compId: c.componentId as string,
        id: c.id as string,
      }));
    const hasNext = !!response.pagingMetadata?.hasNext;
    return {
      connectors,
      hasNext,
      nextCursor: hasNext ? response.pagingMetadata?.cursors?.next : undefined,
    };
  }

  public async findConnectorId(
    appId: string,
    compId: string,
  ): Promise<string | undefined> {
    const response = await this.queryConnectors({
      filter: { appId, componentId: compId },
      limit: 1,
    });
    return response.connectors?.[0]?.id;
  }

  public async startConnect({
    compId,
    appId,
    exposure,
    redirectUrl,
  }: ConnectPayload): Promise<{ authorizationUrl?: string }> {
    const response = await this.request<
      Record<string, unknown>,
      { authorizationUrl?: string }
    >({
      methodName: 'startConnect',
      payload: {
        appId,
        componentId: compId,
        ...(exposure ? { exposure } : {}),
        ...(redirectUrl ? { redirectUrl } : {}),
      },
    });
    return { authorizationUrl: response.authorizationUrl };
  }

  public async deleteConnector(connectorId: string): Promise<void> {
    await this.request<{ connectorId: string }, unknown>({
      methodName: 'deleteConnector',
      payload: { connectorId },
    });
  }

  private async queryConnectors({
    filter,
    cursor,
    limit = PAGE_LIMIT,
  }: {
    filter?: Record<string, unknown>;
    cursor?: string;
    limit?: number;
  }): Promise<QueryConnectorsResponse> {
    return this.request<
      {
        query: {
          filter?: Record<string, unknown>;
          cursorPaging: { limit: number; cursor?: string };
        };
      },
      QueryConnectorsResponse
    >({
      methodName: 'queryConnectors',
      payload: {
        query: {
          ...(filter ? { filter } : {}),
          cursorPaging: { limit, ...(cursor ? { cursor } : {}) },
        },
      },
    });
  }
}
